import { useState } from 'react'
import { format, subDays } from 'date-fns'
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useAnalytics, useLimits } from '../api/analytics'
import Layout from '../components/Layout'
import DateRangePicker from '../components/DateRangePicker'
import MetricCard from '../components/MetricCard'
import UsageCard from '../components/UsageCard'

// Terminal Noir color palette for charts
const VERDICT_COLORS = {
  approved: '#3fb950',
  rejected: '#f85149',
  inconclusive: '#d29922',
  error: '#8b949e',
}

const LINE_COLOR = '#58a6ff'

export default function Dashboard() {
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  })

  const { data, isLoading, error } = useAnalytics({
    start_date: dateRange.start,
    end_date: dateRange.end,
    aggregation: 'daily',
  })

  const { data: limitsData, isLoading: limitsLoading, error: limitsError } = useLimits()

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div
              className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
              aria-label="Loading analytics"
            >
              <span className="sr-only">Loading analytics...</span>
            </div>
            <p className="mt-4 text-sm text-text-secondary">Loading your analytics data...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="rounded-md bg-verdict-rejected/10 border border-verdict-rejected/30 p-6">
          <div className="flex">
            <div className="shrink-0">
              <svg
                className="h-5 w-5 text-verdict-rejected"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-verdict-rejected">Error loading analytics</h3>
              <p className="mt-2 text-sm text-verdict-rejected">
                {error instanceof Error ? error.message : 'Failed to load analytics'}
              </p>
            </div>
          </div>
        </div>
      </Layout>
    )
  }

  const summary = data?.summary
  const analytics = data?.analytics || []

  // Check for empty state
  const hasData = summary && summary.total_requests > 0

  // Prepare pie chart data (excluding errors - users can see those in request list)
  const verdictData = [
    { name: 'Approved', value: summary?.total_approved || 0, color: VERDICT_COLORS.approved },
    { name: 'Rejected', value: summary?.total_rejected || 0, color: VERDICT_COLORS.rejected },
    { name: 'Inconclusive', value: summary?.total_inconclusive || 0, color: VERDICT_COLORS.inconclusive },
  ].filter((d) => d.value > 0)

  // Prepare line chart data for request trend
  const requestTrendData = analytics.map((day) => ({
    date: format(new Date(day.date), 'MMM d'),
    requests: day.total_requests,
  }))

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-text-primary">Analytics Overview</h1>
          <DateRangePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            onChange={(start, end) => setDateRange({ start, end })}
          />
        </div>

        {/* Usage Overview - Always Show */}
        <div className="animate-slide-up stagger-1">
          {limitsLoading ? (
            <div className="bg-noir-secondary border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="h-6 w-32 bg-noir-tertiary rounded-sm animate-pulse"></div>
                <div className="h-5 w-20 bg-noir-tertiary rounded-sm animate-pulse"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="h-4 w-24 bg-noir-tertiary rounded-sm animate-pulse mb-2"></div>
                  <div className="h-8 w-32 bg-noir-tertiary rounded-sm animate-pulse mb-3"></div>
                  <div className="h-3 w-full bg-noir-tertiary rounded-sm animate-pulse"></div>
                </div>
                <div>
                  <div className="h-4 w-28 bg-noir-tertiary rounded-sm animate-pulse mb-2"></div>
                  <div className="h-8 w-32 bg-noir-tertiary rounded-sm animate-pulse mb-3"></div>
                  <div className="h-3 w-full bg-noir-tertiary rounded-sm animate-pulse"></div>
                </div>
              </div>
            </div>
          ) : limitsError ? (
            <div className="bg-noir-secondary border border-verdict-rejected/30 rounded-lg p-6">
              <p className="text-verdict-rejected text-sm">Failed to load usage data. Please refresh the page.</p>
            </div>
          ) : limitsData ? (
            <UsageCard data={limitsData} />
          ) : null}
        </div>

        {!hasData ? (
          <div className="card py-16">
            <div className="text-center">
              <svg
                className="mx-auto h-12 w-12 text-text-secondary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-text-primary">No data for this period</h3>
              <p className="mt-1 text-sm text-text-secondary">
                Try selecting a different date range or start using CodeVet to see your analytics.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Metrics Row - 2 columns */}
            <div className="animate-slide-up stagger-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <MetricCard
                  title="Approval Rate"
                  value={`${((summary?.approval_rate || 0) * 100).toFixed(1)}%`}
                />
                <MetricCard
                  title="Total Requests"
                  value={summary?.total_requests?.toLocaleString() || '0'}
                />
              </div>
            </div>

            {/* Charts Row - 2 columns */}
            <div className="animate-slide-up stagger-3">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Verdict Distribution */}
                <div className="card">
                  <h2 className="text-lg font-medium text-text-primary mb-4">
                    Verdict Distribution
                  </h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={verdictData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) =>
                            `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                          }
                        >
                          {verdictData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#161b22',
                            border: '1px solid #30363d',
                            borderRadius: '6px',
                            color: '#e6edf3',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Request Trend */}
                <div className="card">
                  <h2 className="text-lg font-medium text-text-primary mb-4">
                    Request Trend
                  </h2>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={requestTrendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#30363d" />
                        <XAxis
                          dataKey="date"
                          fontSize={12}
                          stroke="#8b949e"
                          tick={{ fill: '#8b949e' }}
                        />
                        <YAxis
                          fontSize={12}
                          stroke="#8b949e"
                          tick={{ fill: '#8b949e' }}
                        />
                        <Tooltip
                          formatter={(value: number | undefined) => [value ?? 0, 'Requests']}
                          contentStyle={{
                            backgroundColor: '#161b22',
                            border: '1px solid #30363d',
                            borderRadius: '6px',
                            color: '#e6edf3',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="requests"
                          stroke={LINE_COLOR}
                          strokeWidth={2}
                          dot={{ fill: LINE_COLOR }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}
